import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import type { FragrancePartner, PartnerSalesStats, CommissionRule } from '@/types/fragrance';
import { uploadImage } from './firestore/utils';
import { db } from './firebase';
import { initializePartnerFinanceBalance } from './fragranceFinanceService';

// Collection references
const partnersCollection = collection(db, 'fragrance_partners');
const partnerStatsCollection = collection(db, 'fragrance_partner_stats');
const commissionRulesCollection = collection(db, 'fragrance_commission_rules');
const usersCollection = collection(db, 'users');

// Partner functions
export async function getPartners() {
  console.log('Getting all partners from collection:', partnersCollection.path);
  
  try {
    const snapshot = await getDocs(query(partnersCollection, orderBy('name')));
    console.log('Partners query results:', snapshot.size, 'docs found');
    
    const partners = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragrancePartner));
    console.log('Parsed partners:', partners);
    
    return partners;
  } catch (error) {
    console.error('Error fetching partners:', error);
    throw error;
  }
}

export async function getPartner(partnerId: string) {
  const docRef = doc(partnersCollection, partnerId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    throw new Error('Partner not found');
  }
  
  return { id: docSnap.id, ...docSnap.data() } as FragrancePartner;
}

// Get partner by their partner code (3-letter code)
export async function getPartnerByCode(partnerCode: string): Promise<FragrancePartner> {
  try {
    console.log(`Getting partner by code: ${partnerCode}`);
    
    // Query partners collection to find the partner with the matching code
    const partnerQuery = query(partnersCollection, where('code', '==', partnerCode));
    const partnerSnapshot = await getDocs(partnerQuery);
    
    if (partnerSnapshot.empty) {
      console.error(`No partner found with code: ${partnerCode}`);
      throw new Error(`Partner not found with code: ${partnerCode}`);
    }
    
    // Get the first matching partner (there should only be one)
    const partnerDoc = partnerSnapshot.docs[0];
    const partnerData = { id: partnerDoc.id, ...partnerDoc.data() } as FragrancePartner;
    
    console.log(`Found partner: ${partnerData.name} (${partnerData.id}) for code ${partnerCode}`);
    return partnerData;
  } catch (error) {
    console.error(`Error getting partner by code ${partnerCode}:`, error);
    throw error;
  }
}

// Get a partner's code by their user ID
export async function getPartnerCode(userId: string): Promise<string | null> {
  try {
    console.log(`Getting partner code for user ID: ${userId}`);
    
    if (!userId) {
      console.error('Invalid userId provided to getPartnerCode:', userId);
      return null;
    }
    
    // First, try to get the partner code from the user document
    const userRef = doc(usersCollection, userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const partnerCode = userData?.partnerCode;
      
      if (partnerCode) {
        console.log(`Found partner code in user document for ${userId}: ${partnerCode}`);
        return partnerCode;
      } else {
        console.log(`User document exists but no partnerCode field for ${userId}`);
      }
    } else {
      console.log(`User document does not exist for ${userId}`);
    }
    
    // If not found in user document, try to find it in the partners collection
    console.log(`Searching for partner with userId: ${userId}`);
    const partnerQuery = query(partnersCollection, where('userId', '==', userId));
    const partnerSnapshot = await getDocs(partnerQuery);
    
    if (!partnerSnapshot.empty) {
      // Get the first matching partner (there should only be one)
      const partnerDoc = partnerSnapshot.docs[0];
      const partnerData = partnerDoc.data() as FragrancePartner;
      
      if (partnerData.code) {
        // Partner found with a code - update the user document for future use
        console.log(`Found partner code in partners collection for ${userId}: ${partnerData.code}`);
        
        try {
          // Update the user document with the partner code for future reference
          await updateDoc(userRef, {
            partnerCode: partnerData.code,
            updatedAt: serverTimestamp()
          });
          console.log(`Updated user document with partnerCode ${partnerData.code}`);
        } catch (updateError) {
          console.error(`Failed to update user document with partnerCode:`, updateError);
          // Continue anyway, as we found the code
        }
        
        return partnerData.code;
      } else {
        console.log(`Partner found for user ${userId} but has no code`);
      }
    } else {
      console.log(`No partner found with userId: ${userId}`);
    }
    
    // As a last resort, look for a partner where the name or email matches the user's details
    try {
      // Get user details
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userEmail = userData?.email;
        const userName = userData?.name;
        
        if (userEmail || userName) {
          console.log(`Trying to match partner by email or name for user ${userId}`);
          
          // Try to find matching partner by email
          if (userEmail) {
            const emailQuery = query(partnersCollection, where('contactEmail', '==', userEmail));
            const emailSnapshot = await getDocs(emailQuery);
            
            if (!emailSnapshot.empty) {
              const partnerDoc = emailSnapshot.docs[0];
              const partnerData = partnerDoc.data() as FragrancePartner;
              
              if (partnerData.code) {
                console.log(`Found partner with matching email: ${partnerData.code}`);
                
                // Update user document
                await updateDoc(userRef, {
                  partnerCode: partnerData.code,
                  updatedAt: serverTimestamp()
                });
                
                return partnerData.code;
              }
            }
          }
          
          // Try to find matching partner by name
          if (userName) {
            const nameQuery = query(partnersCollection, where('name', '==', userName));
            const nameSnapshot = await getDocs(nameQuery);
            
            if (!nameSnapshot.empty) {
              const partnerDoc = nameSnapshot.docs[0];
              const partnerData = partnerDoc.data() as FragrancePartner;
              
              if (partnerData.code) {
                console.log(`Found partner with matching name: ${partnerData.code}`);
                
                // Update user document
                await updateDoc(userRef, {
                  partnerCode: partnerData.code,
                  updatedAt: serverTimestamp()
                });
                
                return partnerData.code;
              }
            }
          }
        }
      }
    } catch (matchError) {
      console.error('Error trying to match partner by user details:', matchError);
    }
    
    console.log(`No partner code found for userId: ${userId} after all attempts`);
    return null;
  } catch (error) {
    console.error(`Error getting partner code for userId ${userId}:`, error);
    return null;
  }
}

export async function getActivePartners() {
  const q = query(partnersCollection, where('isActive', '==', true), orderBy('name'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FragrancePartner));
}

export async function createPartner(data: Omit<FragrancePartner, 'id' | 'createdAt' | 'updatedAt'>) {
  // Ensure code is uppercase and only 3 letters
  const code = data.code.toUpperCase().slice(0, 3);
  
  // Check if code is already in use
  const codeQuery = query(partnersCollection, where('code', '==', code));
  const codeSnapshot = await getDocs(codeQuery);
  
  if (!codeSnapshot.empty) {
    throw new Error(`Partner code ${code} is already in use`);
  }
  
  // Create the partner
  const partnerData = {
    ...data,
    code,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  
  const docRef = await addDoc(partnersCollection, partnerData);
  
  // Initialize stats document for the partner
  await addDoc(partnerStatsCollection, {
    partnerId: docRef.id,
    partnerName: data.name,
    totalSales: 0,
    totalRedeemed: 0,
    partnerRevenue: 0,
    storeRevenue: 0,
    salesBySource: {
      // Initialize with empty object for dynamic sources
      otherSales: 0
    },
    revenueBySource: {
      // Initialize with empty object for dynamic sources
      otherRevenue: 0
    },
    productSales: {},
    lastUpdated: serverTimestamp(),
  });
  
  // Initialize finance balance for the partner
  try {
    await initializePartnerFinanceBalance(docRef.id, data.name, code);
    console.log(`Initialized finance balance for partner ${data.name}`);
  } catch (error) {
    console.error(`Error initializing finance balance for partner:`, error);
    // Don't fail the partner creation if finance balance fails
  }
  
  return { id: docRef.id, ...partnerData };
}

export async function updatePartner(partnerId: string, data: Partial<Omit<FragrancePartner, 'id' | 'createdAt' | 'updatedAt'>>) {
  const partnerRef = doc(partnersCollection, partnerId);
  
  // If code is being updated, ensure it's uppercase and only 3 letters
  if (data.code) {
    const code = data.code.toUpperCase().slice(0, 3);
    
    // Check if code is already in use by another partner
    const codeQuery = query(partnersCollection, where('code', '==', code));
    const codeSnapshot = await getDocs(codeQuery);
    
    if (!codeSnapshot.empty && codeSnapshot.docs[0].id !== partnerId) {
      throw new Error(`Partner code ${code} is already in use`);
    }
    
    data.code = code;
  }
  
  await updateDoc(partnerRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
  
  // If name is updated, also update the name in stats
  if (data.name) {
    const statsQuery = query(partnerStatsCollection, where('partnerId', '==', partnerId));
    const statsSnapshot = await getDocs(statsQuery);
    
    statsSnapshot.docs.forEach(async (statDoc) => {
      await updateDoc(doc(partnerStatsCollection, statDoc.id), {
        partnerName: data.name,
        updatedAt: serverTimestamp(),
      });
    });
  }
  
  const updatedDoc = await getDoc(partnerRef);
  return { id: updatedDoc.id, ...updatedDoc.data() } as FragrancePartner;
}

export async function deletePartner(partnerId: string) {
  // Check if partner has any products or promotions
  // This would require importing other collections and checking for references
  // For simplicity, we'll just delete the partner for now
  
  await deleteDoc(doc(partnersCollection, partnerId));
  
  // Delete partner stats
  const statsQuery = query(partnerStatsCollection, where('partnerId', '==', partnerId));
  const statsSnapshot = await getDocs(statsQuery);
  
  const deletePromises = statsSnapshot.docs.map(statDoc => 
    deleteDoc(doc(partnerStatsCollection, statDoc.id))
  );
  
  await Promise.all(deletePromises);
  
  return { success: true };
}

export async function getPartnerStats(partnerId: string): Promise<PartnerSalesStats | null> {
  console.log('Getting stats for partner ID:', partnerId);
  
  if (!partnerId) {
    console.error('Invalid partnerId provided:', partnerId);
    return null;
  }
  
  const statsQuery = query(partnerStatsCollection, where('partnerId', '==', partnerId));
  console.log('Partner stats query created with collection:', partnerStatsCollection.path);
  
  try {
    const statsSnapshot = await getDocs(statsQuery);
    console.log('Stats query results:', statsSnapshot.size, 'docs found');
    
    if (statsSnapshot.empty) {
      console.log('No stats found for partner ID:', partnerId);
      
      // Check if partner exists first
      try {
        // Try to get the partner, but don't throw if not found
        const partnerQuery = query(partnersCollection, where('code', '==', partnerId));
        const partnerSnapshot = await getDocs(partnerQuery);
        
        // If partner exists, create stats for them
        if (!partnerSnapshot.empty) {
          const partner = { id: partnerSnapshot.docs[0].id, ...partnerSnapshot.docs[0].data() } as FragrancePartner;
          
          const newStats: Omit<PartnerSalesStats, 'lastUpdated'> = {
            partnerId,
            partnerName: partner.name,
            totalSales: 0,
            totalRedeemed: 0,
            partnerRevenue: 0,
            storeRevenue: 0,
            salesBySource: {
              otherSales: 0
            },
            revenueBySource: {
              otherRevenue: 0
            },
            productSales: {},
          };
          
          const docRef = await addDoc(partnerStatsCollection, {
            ...newStats,
            lastUpdated: serverTimestamp(),
          });
          
          console.log('Created new stats document for partner:', partnerId, 'with ID:', docRef.id);
          
          return { id: docRef.id, ...newStats } as PartnerSalesStats & { id: string };
        } else {
          // If partner doesn't exist, return default empty stats without saving to database
          console.log('Partner not found, returning default empty stats');
          return {
            partnerId,
            partnerName: `Partner ${partnerId}`,
            totalSales: 0,
            totalRedeemed: 0,
            partnerRevenue: 0,
            storeRevenue: 0,
            salesBySource: { otherSales: 0 },
            revenueBySource: { otherRevenue: 0 },
            productSales: {},
            lastUpdated: null
          } as PartnerSalesStats;
        }
      } catch (error) {
        console.error('Error checking partner or creating stats document:', error);
        // Return default stats object even if there's an error
        return {
          partnerId,
          partnerName: `Partner ${partnerId}`,
          totalSales: 0,
          totalRedeemed: 0,
          partnerRevenue: 0,
          storeRevenue: 0,
          salesBySource: { otherSales: 0 },
          revenueBySource: { otherRevenue: 0 },
          productSales: {},
          lastUpdated: null
        } as PartnerSalesStats;
      }
    }
    
    console.log('Stats doc ID:', statsSnapshot.docs[0].id);
    const data = statsSnapshot.docs[0].data();
    console.log('Stats data:', data);
    
    return { id: statsSnapshot.docs[0].id, ...data } as PartnerSalesStats & { id: string };
  } catch (error) {
    console.error('Error fetching partner stats:', error);
    throw error;
  }
}

export async function getAllPartnerStats(): Promise<(PartnerSalesStats & { id: string })[]> {
  const statsSnapshot = await getDocs(partnerStatsCollection);
  return statsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as PartnerSalesStats & { id: string }));
}

export async function initializeAllPartnerStats(): Promise<void> {
  try {
    // Get all partners
    const partnersData = await getPartners();
    console.log(`Initializing stats for ${partnersData.length} partners`);
    
    // Get existing partner stats documents
    const statsSnapshot = await getDocs(partnerStatsCollection);
    const existingPartnerIds = statsSnapshot.docs.map(doc => doc.data().partnerId);
    
    // Filter partners that don't have a stats document
    const partnersWithoutStats = partnersData.filter(partner => 
      !existingPartnerIds.includes(partner.id!)
    );
    
    console.log(`Found ${partnersWithoutStats.length} partners without stats documents`);
    
    // Create stats documents for partners that don't have one
    const promises = partnersWithoutStats.map(async partner => {
      const newStats: Omit<PartnerSalesStats, 'lastUpdated'> = {
        partnerId: partner.id!,
        partnerName: partner.name,
        totalSales: 0,
        totalRedeemed: 0,
        partnerRevenue: 0,
        storeRevenue: 0,
        salesBySource: {
          otherSales: 0
        },
        revenueBySource: {
          otherRevenue: 0
        },
        productSales: {},
      };
      
      const docRef = await addDoc(partnerStatsCollection, {
        ...newStats,
        lastUpdated: serverTimestamp(),
      });
      
      console.log(`Created stats document for partner ${partner.name} (${partner.id}) with ID: ${docRef.id}`);
    });
    
    await Promise.all(promises);
    console.log('Partner stats initialization complete');
  } catch (error) {
    console.error('Error initializing partner stats:', error);
    throw error;
  }
}

// Helper function to update partner stats when a code is sold, redeemed, or returned
export async function updatePartnerStats(
  partnerId: string,
  action: 'sold' | 'redeemed' | 'return_sold' | 'return_redeemed',
  saleSource: string,
  price: number,
  givePartnerCommission: boolean,
  productId?: string,
  isAdminInitiated: boolean = false,
  commissionAmount?: number // Optional parameter for specifying exact commission amount for returns
) {
  console.log(`updatePartnerStats called for partnerId: ${partnerId}, action: ${action}, price: ${price}, giveCommission: ${givePartnerCommission}, isAdminInitiated: ${isAdminInitiated}`);
  
  try {
    // Check if partnerId is a 3-letter code or an actual document ID
    let partner: FragrancePartner;
    
    // If partnerId is 3 characters, assume it's a partner code
    if (partnerId.length <= 3) {
      console.log(`Treating ${partnerId} as a partner code`);
      partner = await getPartnerByCode(partnerId);
    } else {
      // Otherwise, assume it's a document ID
      console.log(`Treating ${partnerId} as a partner document ID`);
      partner = await getPartner(partnerId);
    }
    
    console.log(`Retrieved partner: ${partner.name} (${partner.id})`);
    
    // Get commission percentages and rules for this partner
    const { 
      initialCommissionPercentage, 
      redemptionCommissionPercentage,
      allowPartnerInitiatedCommission,
      allowAdminInitiatedCommission,
      allowRedemptionCommission
    } = await getPartnerCommissionPercentages(partnerId);
    
    console.log(`Commission rules - Initial: ${initialCommissionPercentage * 100}%`);
    console.log(`- Redemption commission: ${redemptionCommissionPercentage * 100}%`);
    console.log(`- Allow Partner: ${allowPartnerInitiatedCommission}`);
    console.log(`- Allow Admin: ${allowAdminInitiatedCommission}`);
    console.log(`- Allow redemption commission: ${allowRedemptionCommission}`);
    console.log(`- Sale source: ${saleSource}`);
    console.log(`- Is admin initiated: ${isAdminInitiated}`);
    console.log(`- Give commission flag: ${givePartnerCommission}`);
    
    // Calculate the partner's share based on commission flags and percentages
    let partnerRevenue = 0;
    let storeRevenue = 0;
    let countSaleInStats = true; // Always count the sale in statistics
    let giveCommission = givePartnerCommission;
    
    // For return actions, we'll use the provided commission amount or calculate it
    if (action === 'return_sold' || action === 'return_redeemed') {
      // For returns, we're reversing the commission
      if (commissionAmount !== undefined) {
        // Use the provided commission amount if available
        partnerRevenue = -commissionAmount; // Negative to reverse the commission
        storeRevenue = commissionAmount; // Positive for the store (getting money back)
        console.log(`Using provided commission amount for return: ${commissionAmount}`);
      } else {
        // Calculate based on percentages if no specific amount provided
        if (action === 'return_sold') {
          // Reverse the initial commission
          partnerRevenue = -(price * initialCommissionPercentage);
          storeRevenue = -partnerRevenue;
          console.log(`Calculated return commission for sold code: ${partnerRevenue}`);
        } else if (action === 'return_redeemed') {
          // Reverse both initial and redemption commission
          const totalCommission = price * (initialCommissionPercentage + redemptionCommissionPercentage);
          partnerRevenue = -totalCommission;
          storeRevenue = -partnerRevenue;
          console.log(`Calculated return commission for redeemed code: ${partnerRevenue}`);
        }
      }
    } else {
      // Regular sales and redemptions
      // Check if commission is allowed based on who initiated the action and the sale source
      const isTelUAseghSale = saleSource === 'TEL U ASEGH' || saleSource === 'tel_u_asegh';
      
      // For sales, check if commission is allowed based on who initiated the action
      let giveSalesCommission = giveCommission;
      
      // For redemptions, always start with the assumption that redemption commission is allowed
      // This makes redemption commission completely independent of sales commission
      let giveRedemptionCommission = true;
      
      // For sales initiated by admin
      if (action === 'sold' && isAdminInitiated && !allowAdminInitiatedCommission) {
        giveSalesCommission = false; // No sales commission for admin-initiated sales if not allowed
        console.log('Admin-initiated sale but commission not allowed by rule');
      } 
      // For sales initiated by partner
      else if (action === 'sold' && !isAdminInitiated && !allowPartnerInitiatedCommission) {
        giveSalesCommission = false; // No sales commission for partner-initiated sales if not allowed
        console.log('Partner-initiated sale but commission not allowed by rule');
      }
      
      // For redemptions, check if redemption commission is allowed
      // This is completely independent of the sales commission settings
      if (action === 'redeemed' && !allowRedemptionCommission) {
        giveRedemptionCommission = false; // No redemption commission if not allowed
        console.log('Redemption commission not allowed by rule');
      }
      
      // Log the commission decision
      if (action === 'redeemed') {
        console.log(`Redemption commission decision: ${giveRedemptionCommission ? 'ALLOWED' : 'NOT ALLOWED'}`);
      }
      
      // Calculate revenue based on action type and commission flags
      if (action === 'sold' && giveSalesCommission) {
        // For sales with commission allowed
        partnerRevenue = price * initialCommissionPercentage;
        storeRevenue = price - partnerRevenue;
        console.log(`Calculated commission for sold code: ${partnerRevenue} (${initialCommissionPercentage * 100}% of ${price})`);
      } else if (action === 'redeemed' && giveRedemptionCommission) {
        // For redemptions with commission allowed - independent of whether sales commission was given
        partnerRevenue = price * redemptionCommissionPercentage;
        storeRevenue = -partnerRevenue; // Store gives up this amount
        console.log(`Calculated additional commission for redeemed code: ${partnerRevenue} (${redemptionCommissionPercentage * 100}% of ${price})`);
      } else if (action === 'sold') {
        // If no commission for sales, store gets all revenue
        storeRevenue = price;
        console.log(`No commission given for sale, store gets full price: ${price}`);
      } else if (action === 'redeemed') {
        // If no commission for redemption, no additional revenue changes
        console.log(`No commission given for redemption`);
      }
    }
    
    console.log(`Calculated revenues - Partner: ${partnerRevenue}, Store: ${storeRevenue}`);
    
    // Get the stats document for this partner
    const statsQuery = query(partnerStatsCollection, where('partnerId', '==', partnerId));
    const statsSnapshot = await getDocs(statsQuery);
    
    // Create or update the stats document
    if (statsSnapshot.empty) {
      // Create a new stats document with initial values
      console.log(`No stats document found for partnerId: ${partnerId}, creating new one with initial values`);
      
      const initialStats: any = {
        partnerId,
        partnerName: partner.name,
        totalSales: action === 'sold' ? 1 : 0,
        totalRedeemed: action === 'redeemed' ? 1 : 0,
        partnerRevenue: partnerRevenue,
        storeRevenue: storeRevenue,
        lastUpdated: serverTimestamp(),
      };
      
      // Initialize salesBySource with the current sale
      initialStats.salesBySource = {};
      if (action === 'sold') {
        if (saleSource) {
          initialStats.salesBySource[saleSource] = 1;
        } else {
          initialStats.salesBySource.otherSales = 1;
        }
      }
      
      // Initialize revenueBySource with the current sale
      initialStats.revenueBySource = {};
      if (action === 'sold') {
        if (saleSource) {
          initialStats.revenueBySource[saleSource] = price;
        } else {
          initialStats.revenueBySource.otherRevenue = price;
        }
      }
      
      // Initialize productSales with the current product
      initialStats.productSales = {};
      if (action === 'sold' && productId) {
        initialStats.productSales[productId] = 1;
      }
      
      // Create the document with correct initial values
      const docRef = await addDoc(partnerStatsCollection, initialStats);
      console.log(`Created new stats document with ID: ${docRef.id} and initial values`);
    } else {
      // Update existing stats using atomic operations
      const statsDocRef = doc(partnerStatsCollection, statsSnapshot.docs[0].id);
      console.log(`Updating existing stats document: ${statsDocRef.path} with atomic operations`);
      
      const updateData: any = {
        lastUpdated: serverTimestamp(),
      };
      
      if (action === 'sold') {
        // Use increment for all numeric fields
        updateData.totalSales = increment(1);
        updateData.partnerRevenue = increment(partnerRevenue);
        updateData.storeRevenue = increment(storeRevenue);
        
        // Update sales by source using increment
        if (saleSource) {
          updateData[`salesBySource.${saleSource}`] = increment(1);
          updateData[`revenueBySource.${saleSource}`] = increment(price);
        } else {
          updateData[`salesBySource.otherSales`] = increment(1);
          updateData[`revenueBySource.otherRevenue`] = increment(price);
        }
        
        // Update product sales using increment
        if (productId) {
          updateData[`productSales.${productId}`] = increment(1);
        }
      } else if (action === 'redeemed') {
        // For redemptions
        updateData.totalRedeemed = increment(1);
        updateData.partnerRevenue = increment(partnerRevenue);
        updateData.storeRevenue = increment(storeRevenue);
      }
      
      console.log(`Update data:`, JSON.stringify(updateData, null, 2));
      
      // Update the partner stats document
      await updateDoc(doc(partnerStatsCollection, statsSnapshot.docs[0].id), {
        ...updateData,
        lastUpdated: serverTimestamp()
      });
    }
    
    console.log(`Partner stats updated successfully for partner ${partnerId}`);
    return true;
  } catch (error) {
    console.error(`Error updating stats for partnerId: ${partnerId}`, error);
    throw error;
  }
}

// Commission Rules functions
export async function getCommissionRules() {
  const snapshot = await getDocs(query(commissionRulesCollection, orderBy('createdAt', 'desc')));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CommissionRule));
}

export async function getPartnerCommissionRules(partnerId: string) {
  const q = query(
    commissionRulesCollection, 
    where('partnerId', '==', partnerId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CommissionRule));
}

export async function getActiveCommissionRule(partnerId: string): Promise<CommissionRule | null> {
  const q = query(
    commissionRulesCollection, 
    where('partnerId', '==', partnerId),
    where('active', '==', true),
    orderBy('createdAt', 'desc'),
  );
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  // Return the most recent active rule
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as CommissionRule;
}

export async function createCommissionRule(data: Omit<CommissionRule, 'id' | 'createdAt' | 'updatedAt'>) {
  // Check if partner already has an active rule
  const existingRulesQuery = query(
    commissionRulesCollection,
    where('partnerId', '==', data.partnerId),
    where('active', '==', true)
  );
  
  const existingRulesSnapshot = await getDocs(existingRulesQuery);
  
  if (!existingRulesSnapshot.empty && data.active) {
    throw new Error(`Partner already has an active commission rule. Please deactivate the existing rule first.`);
  }
  
  // If this is set as active, deactivate other rules for this partner
  if (data.active) {
    await deactivatePartnerRules(data.partnerId);
  }
  
  const ruleData = {
    ...data,
    initialCommissionPercentage: Number(data.initialCommissionPercentage),
    redemptionCommissionPercentage: Number(data.redemptionCommissionPercentage),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  
  const docRef = await addDoc(commissionRulesCollection, ruleData);
  return { id: docRef.id, ...ruleData };
}

export async function updateCommissionRule(
  ruleId: string, 
  data: Partial<Omit<CommissionRule, 'id' | 'createdAt' | 'updatedAt'>>
) {
  const ruleRef = doc(commissionRulesCollection, ruleId);
  
  // If setting as active, deactivate other rules for this partner
  if (data.active) {
    // Get the current rule to know which partner to update
    const currentRule = await getDoc(ruleRef);
    if (currentRule.exists()) {
      const partnerId = currentRule.data().partnerId;
      await deactivatePartnerRules(partnerId);
    }
  }
  
  const updateData = {
    ...data,
    updatedAt: serverTimestamp(),
  };
  
  // Convert percentages to numbers if they exist
  if (data.initialCommissionPercentage !== undefined) {
    updateData.initialCommissionPercentage = Number(data.initialCommissionPercentage);
  }
  
  if (data.redemptionCommissionPercentage !== undefined) {
    updateData.redemptionCommissionPercentage = Number(data.redemptionCommissionPercentage);
  }
  
  await updateDoc(ruleRef, updateData);
  
  const updatedDoc = await getDoc(ruleRef);
  return { id: updatedDoc.id, ...updatedDoc.data() } as CommissionRule;
}

export async function deleteCommissionRule(ruleId: string) {
  await deleteDoc(doc(commissionRulesCollection, ruleId));
  return { success: true };
}

// Helper function to deactivate all rules for a partner
async function deactivatePartnerRules(partnerId: string) {
  const q = query(
    commissionRulesCollection, 
    where('partnerId', '==', partnerId),
    where('active', '==', true)
  );
  const snapshot = await getDocs(q);
  
  const updatePromises = snapshot.docs.map(ruleDoc => 
    updateDoc(doc(commissionRulesCollection, ruleDoc.id), {
      active: false,
      updatedAt: serverTimestamp(),
    })
  );
  
  await Promise.all(updatePromises);
}

// Helper function to get commission percentages and rules for a partner
export async function getPartnerCommissionPercentages(partnerId: string) {
  // Default values if no rule is found
  const defaultValues = {
    initialCommissionPercentage: 0.1, // 10%
    redemptionCommissionPercentage: 0.15, // 15%
    allowPartnerInitiatedCommission: true,
    allowAdminInitiatedCommission: true,
    allowRedemptionCommission: true
  };
  
  try {
    // If partnerId is a 3-letter code, get the actual partner ID
    let actualPartnerId = partnerId;
    if (partnerId.length <= 3) {
      try {
        const partner = await getPartnerByCode(partnerId);
        actualPartnerId = partner.id!;
        console.log(`Resolved partner code ${partnerId} to partner ID ${actualPartnerId}`);
      } catch (error) {
        console.error(`Error resolving partner code ${partnerId}:`, error);
        // Continue with the original partnerId
      }
    }
    
    // Try to get an active rule for this partner
    const activeRule = await getActiveCommissionRule(actualPartnerId);
    
    if (!activeRule) {
      console.log(`No active commission rule found for partner ${partnerId}, using default values`);
      return defaultValues;
    }
    
    console.log(`Found active commission rule for partner ${partnerId}:`, {
      name: activeRule.name,
      initialCommission: activeRule.initialCommissionPercentage,
      redemptionCommission: activeRule.redemptionCommissionPercentage,
      allowPartner: activeRule.allowPartnerInitiatedCommission,
      allowAdmin: activeRule.allowAdminInitiatedCommission,
      allowRedemption: activeRule.allowRedemptionCommission
    });
    
    // If allowRedemptionCommission is false, set redemptionCommissionPercentage to 0
    const redemptionPercentage = activeRule.allowRedemptionCommission ? 
      activeRule.redemptionCommissionPercentage / 100 : 0;
    
    return {
      initialCommissionPercentage: activeRule.initialCommissionPercentage / 100, // Convert from percentage to decimal
      redemptionCommissionPercentage: redemptionPercentage,
      allowPartnerInitiatedCommission: activeRule.allowPartnerInitiatedCommission,
      allowAdminInitiatedCommission: activeRule.allowAdminInitiatedCommission,
      allowRedemptionCommission: activeRule.allowRedemptionCommission
    };
  } catch (error) {
    console.error(`Error getting commission percentages for partner ${partnerId}:`, error);
    return defaultValues;
  }
}

export async function manuallyUpdatePartnerStats(partnerId: string): Promise<boolean> {
  console.log(`Manually updating stats for partner: ${partnerId}`);
  
  try {
    // First, check if partner exists
    const partner = await getPartner(partnerId);
    console.log(`Partner found: ${partner.name}`);
    
    // Check if stats document exists
    const statsQuery = query(partnerStatsCollection, where('partnerId', '==', partnerId));
    const statsSnapshot = await getDocs(statsQuery);
    
    if (statsSnapshot.empty) {
      console.log(`No stats document found, creating new one`);
      
      // Create new stats document
      const newStats: Omit<PartnerSalesStats, 'lastUpdated'> = {
        partnerId,
        partnerName: partner.name,
        totalSales: 1, // Set to 1 for testing
        totalRedeemed: 0,
        partnerRevenue: 100, // Set to 100 for testing
        storeRevenue: 900, // Set to 900 for testing
        salesBySource: {
          test: 1, // Add test source
          otherSales: 0
        },
        revenueBySource: {
          test: 1000, // Add test revenue
          otherRevenue: 0
        },
        productSales: {},
      };
      
      const docRef = await addDoc(partnerStatsCollection, {
        ...newStats,
        lastUpdated: serverTimestamp(),
      });
      
      console.log(`Created new stats document with ID: ${docRef.id}`);
      return true;
    } else {
      console.log(`Stats document found, updating`);
      
      const statsDocRef = doc(partnerStatsCollection, statsSnapshot.docs[0].id);
      const statsDoc = await getDoc(statsDocRef);
      const stats = statsDoc.data() as PartnerSalesStats;
      
      console.log(`Current stats:`, stats);
      
      // Update with test values
      const updateData = {
        totalSales: (stats.totalSales || 0) + 1,
        partnerRevenue: (stats.partnerRevenue || 0) + 100,
        storeRevenue: (stats.storeRevenue || 0) + 900,
        'salesBySource.test': (stats.salesBySource?.test || 0) + 1,
        'revenueBySource.test': (stats.revenueBySource?.test || 0) + 1000,
        lastUpdated: serverTimestamp(),
      };
      
      console.log(`Updating with:`, updateData);
      await updateDoc(statsDocRef, updateData);
      
      console.log(`Stats document updated successfully`);
      return true;
    }
  } catch (error) {
    console.error(`Error manually updating stats:`, error);
    return false;
  }
}

export async function recalculatePartnerStats(partnerId: string): Promise<boolean> {
  console.log(`Recalculating complete stats for partner: ${partnerId}`);
  
  try {
    // 1. First, verify the partner exists
    const partner = await getPartner(partnerId);
    console.log(`Partner found: ${partner.name}`);
    
    // 2. Find all codes belonging to this partner
    const { getFragranceCodes } = await import('./fragranceService');
    const allCodes = await getFragranceCodes();
    const partnerCodes = allCodes.filter(code => code.partnerId === partnerId);
    console.log(`Found ${partnerCodes.length} codes for this partner`);
    
    if (partnerCodes.length === 0) {
      console.log('No codes found for this partner, nothing to recalculate');
      return false;
    }
    
    // 3. Group the codes by status
    const soldCodes = partnerCodes.filter(code => code.isUsed && !code.isRedeemed);
    const redeemedCodes = partnerCodes.filter(code => code.isRedeemed);
    console.log(`Sold codes: ${soldCodes.length}, Redeemed codes: ${redeemedCodes.length}`);
    
    // 4. Get product details for prices
    const productIds = new Set(partnerCodes.map(code => code.fragranceId));
    const productsData: Record<string, number> = {};
    
    const { getFragranceProduct } = await import('./fragranceService');
    
    for (const productId of productIds) {
      const product = await getFragranceProduct(productId);
      if (product) {
        productsData[productId] = product.price;
      }
    }
    
    console.log('Product prices loaded:', productsData);
    
    // 5. Calculate statistics
    let totalSales = soldCodes.length + redeemedCodes.length;
    let totalRedeemed = redeemedCodes.length;
    let partnerRevenue = 0;
    let storeRevenue = 0;
    
    // 6. Count sales by source
    const salesBySource: { [sourceCode: string]: number; otherSales: number } = { otherSales: 0 };
    const revenueBySource: { [sourceCode: string]: number; otherRevenue: number } = { otherRevenue: 0 };
    const productSales: Record<string, number> = {};
    
    // Get commission percentages for this partner
    const { initialCommissionPercentage, redemptionCommissionPercentage } = 
      await getPartnerCommissionPercentages(partnerId);
    
    console.log(`Commission rates - Initial: ${initialCommissionPercentage * 100}%, Redemption: ${redemptionCommissionPercentage * 100}%`);
    
    // Process all codes first to calculate total revenue by source
    for (const code of partnerCodes) {
      if (code.isUsed) {
        const price = productsData[code.fragranceId] || 0;
        const source = code.saleSource || 'other';
        
        // Update product sales
        productSales[code.fragranceId] = (productSales[code.fragranceId] || 0) + 1;
        
        // Update sales by source
        if (source !== 'other') {
          salesBySource[source] = (salesBySource[source] || 0) + 1;
          revenueBySource[source] = (revenueBySource[source] || 0) + price;
        } else {
          salesBySource.otherSales = (salesBySource.otherSales || 0) + 1;
          revenueBySource.otherRevenue = (revenueBySource.otherRevenue || 0) + price;
        }
      }
    }
    
    // Process sold codes for revenue
    for (const code of soldCodes) {
      const price = productsData[code.fragranceId] || 0;
      
      // Calculate revenue split for sold codes
      if (code.givePartnerCommission) {
        const pRevenue = price * initialCommissionPercentage;
        const sRevenue = price - pRevenue;
        
        partnerRevenue += pRevenue;
        storeRevenue += sRevenue;
      } else {
        storeRevenue += price;
      }
    }
    
    // Process redeemed codes (additional commission for redemption)
    for (const code of redeemedCodes) {
      const price = productsData[code.fragranceId] || 0;
      
      // Calculate base revenue split first (as if it's sold)
      if (code.givePartnerCommission) {
        const initialPartnerRevenue = price * initialCommissionPercentage;
        const initialStoreRevenue = price - initialPartnerRevenue;
        
        // Add base revenue
        partnerRevenue += initialPartnerRevenue;
        storeRevenue += initialStoreRevenue;
        
        // Then add additional commission for redemption
        const additionalCommission = price * redemptionCommissionPercentage;
        partnerRevenue += additionalCommission;
        // Deduct the additional commission from store revenue
        storeRevenue -= additionalCommission;
      } else {
        storeRevenue += price;
      }
    }
    
    // 7. Find or create stats document
    const statsQuery = query(partnerStatsCollection, where('partnerId', '==', partnerId));
    const statsSnapshot = await getDocs(statsQuery);
    
    let statsDocRef;
    if (statsSnapshot.empty) {
      console.log('No stats document found, creating new one');
      
      // Create a new stats document
      const newStats: Omit<PartnerSalesStats, 'lastUpdated'> = {
        partnerId,
        partnerName: partner.name,
        totalSales,
        totalRedeemed,
        partnerRevenue,
        storeRevenue,
        salesBySource,
        revenueBySource,
        productSales,
      };
      
      const docRef = await addDoc(partnerStatsCollection, {
        ...newStats,
        lastUpdated: serverTimestamp(),
      });
      
      console.log(`Created new stats document with ID: ${docRef.id}`);
      statsDocRef = docRef;
    } else {
      // Update existing document
      statsDocRef = doc(partnerStatsCollection, statsSnapshot.docs[0].id);
      
      await updateDoc(statsDocRef, {
        totalSales,
        totalRedeemed,
        partnerRevenue,
        storeRevenue,
        salesBySource,
        revenueBySource,
        productSales,
        lastUpdated: serverTimestamp(),
      });
      
      console.log(`Updated existing stats document with ID: ${statsDocRef.id}`);
    }
    
    console.log('Stats calculation complete with values:', {
      totalSales,
      totalRedeemed,
      partnerRevenue,
      storeRevenue
    });
    
    return true;
  } catch (error) {
    console.error(`Error recalculating partner stats:`, error);
    return false;
  }
}

export async function fixTestPartnerStats(): Promise<boolean> {
  const partnerId = "BORe8rVZ8hO6TlPEKIa0"; // The "test" partner ID
  
  console.log(`Fixing stats for test partner with ID: ${partnerId}`);
  
  try {
    // First, verify the partner exists
    const partner = await getPartner(partnerId);
    console.log(`Partner found: ${partner.name}`);
    
    // Find the partner stats document
    const statsQuery = query(partnerStatsCollection, where('partnerId', '==', partnerId));
    const statsSnapshot = await getDocs(statsQuery);
    
    if (statsSnapshot.empty) {
      console.error('No stats document found for test partner');
      return false;
    }
    
    // Use the known price of 950 EGP
    const productPrice = 950; // Fixed price for ALAVERDI codes
    console.log(`Using fixed price for ALAVERDI codes: ${productPrice} EGP`);
    
    // Get the ALAVERDI product by searching for products with this name
    const { getFragranceProducts } = await import('./fragranceService');
    const products = await getFragranceProducts();
    const alaverdiProduct = products.find(p => p.name === 'ALAVERDI' || p.name.includes('ALAVERDI'));
    
    const productId = alaverdiProduct?.id || 'unknown-product-id';
    console.log(`Found ALAVERDI product with ID: ${productId}`);
    
    // Get commission percentages for this partner
    const { initialCommissionPercentage } = await getPartnerCommissionPercentages(partnerId);
    console.log(`Commission percentage: ${initialCommissionPercentage * 100}%`);
    
    // Calculate the revenue split for 2 sold codes with 1 redemption
    const totalSales = 2;
    const totalRedeemed = 1; // One of the two codes has been redeemed
    
    // Calculate initial commission for both sold codes
    const initialPartnerRevenue = totalSales * productPrice * initialCommissionPercentage;
    
    // Calculate additional commission for the redeemed code
    const { redemptionCommissionPercentage } = await getPartnerCommissionPercentages(partnerId);
    const additionalCommission = totalRedeemed * productPrice * redemptionCommissionPercentage;
    
    // Total partner revenue
    const partnerRevenue = initialPartnerRevenue + additionalCommission;
    
    // Store revenue is total price minus partner revenue
    const storeRevenue = totalSales * productPrice - partnerRevenue;
    
    console.log(`Calculated stats - Sales: ${totalSales}, Redeemed: ${totalRedeemed}`);
    console.log(`Initial partner revenue: ${initialPartnerRevenue} EGP`);
    console.log(`Additional redemption commission: ${additionalCommission} EGP`);
    console.log(`Total partner revenue: ${partnerRevenue} EGP`);
    console.log(`Store revenue: ${storeRevenue} EGP`);
    console.log(`Total revenue: ${partnerRevenue + storeRevenue} EGP`);
    
    // Set fixed data based on the exact 2 codes we know exist
    const statsData = {
      partnerName: partner.name,
      partnerId: partnerId,
      totalSales: totalSales, 
      totalRedeemed: totalRedeemed,
      partnerRevenue: partnerRevenue,
      storeRevenue: storeRevenue,
      salesBySource: {
        // Using 'other' since we don't know the exact saleSource value
        otherSales: totalSales
      },
      revenueBySource: {
        // Using 'other' since we don't know the exact saleSource value
        otherRevenue: totalSales * productPrice
      },
      productSales: {
        // Use the actual product ID if we found it
        [productId]: totalSales
      },
      lastUpdated: serverTimestamp()
    };
    
    // Update the stats document
    const statsDocRef = doc(partnerStatsCollection, statsSnapshot.docs[0].id);
    console.log(`Updating stats document with ID: ${statsDocRef.id} with fixed data`);
    await updateDoc(statsDocRef, statsData);
    
    console.log('Fixed stats document updated successfully with known data');
    return true;
  } catch (error) {
    console.error('Error fixing test partner stats:', error);
    return false;
  }
}

/**
 * Checks for inconsistencies in partner stats by comparing totals to individual items
 * @param partnerId The ID of the partner to check
 * @returns Object with any inconsistencies found
 */
export async function detectPartnerStatsIssues(partnerId: string): Promise<{hasIssues: boolean, issues: string[]}> {
  console.log(`Checking stats consistency for partner: ${partnerId}`);
  
  try {
    // Get the partner stats
    const stats = await getPartnerStats(partnerId);
    if (!stats) {
      return { hasIssues: true, issues: ["No stats document found"] };
    }
    
    const issues: string[] = [];
    
    // Get all codes for this partner
    const { getFragranceCodes } = await import('./fragranceService');
    const allCodes = await getFragranceCodes();
    const partnerCodes = allCodes.filter(code => code.partnerId === partnerId);
    
    // Count sold and redeemed codes
    const soldCodes = partnerCodes.filter(code => code.isUsed && !code.isRedeemed);
    const redeemedCodes = partnerCodes.filter(code => code.isRedeemed);
    
    // Check total sales
    const actualTotalSales = soldCodes.length + redeemedCodes.length;
    if (stats.totalSales !== actualTotalSales) {
      issues.push(`Total sales mismatch: stored ${stats.totalSales}, actual ${actualTotalSales}`);
    }
    
    // Check total redeemed
    if (stats.totalRedeemed !== redeemedCodes.length) {
      issues.push(`Total redeemed mismatch: stored ${stats.totalRedeemed}, actual ${redeemedCodes.length}`);
    }
    
    // Check sales by source
    const salesBySource: Record<string, number> = {};
    partnerCodes.forEach(code => {
      if (code.isUsed) {
        const source = code.saleSource || 'other';
        salesBySource[source] = (salesBySource[source] || 0) + 1;
      }
    });
    
    // Verify each source matches
    Object.entries(salesBySource).forEach(([source, count]) => {
      const storedCount = source === 'other' 
        ? (stats.salesBySource?.otherSales || 0)
        : (stats.salesBySource?.[source] || 0);
        
      if (storedCount !== count) {
        issues.push(`Sales by source mismatch for ${source}: stored ${storedCount}, actual ${count}`);
      }
    });
    
    return {
      hasIssues: issues.length > 0,
      issues
    };
  } catch (error) {
    console.error('Error detecting partner stats issues:', error);
    return {
      hasIssues: true,
      issues: [`Error checking stats: ${error}`]
    };
  }
} 